import React, {createContext, useContext, useReducer, useCallback, useEffect, useRef} from 'react';
import API from '../../../../network/dashboardAPI';
import moment from 'moment';

// Default visible window: yesterday through tomorrow, so orders scheduled just
// outside today are still in view without touching the filters.
const getDefaultDateRange = () => ({
	start: moment().subtract(1, 'day').startOf('day').format('YYYY-MM-DD HH:mm:ss'),
	end: moment().add(1, 'day').endOf('day').format('YYYY-MM-DD HH:mm:ss'),
});

// Server-side sorting by remaining is expensive: the backend prices the whole
// matching set before it can page. It is only requested for a bounded window
// (<= ~6 months, same bound the export enforces). On "All" dates or a wider
// range the Remaining column sorts the loaded rows on the client instead.
export const REMAINING_SORT_MAX_DAYS = 184;
export const canServerSortRemaining = (dateRange) => {
	const {start, end} = dateRange || {};
	if (!start || !end) return false;
	const days = Math.ceil((new Date(end) - new Date(start)) / (1000 * 3600 * 24));
	return days <= REMAINING_SORT_MAX_DAYS;
};

// Initial state
const initialState = {
	// Data
	orders: [],
	selectedOrder: null,
	orderStatusCount: {},

	// Pagination
	page: 0,
	rowsPerPage: 30,
	totalCount: 0,

	// Filters
	statusFilter: '2345678', // All statuses
	searchText: '',
	dateRange: getDefaultDateRange(),
	basketTypeFilter: '',
	// Channel the order was placed through (order_details.source, written
	// server-side from a verified channel token): '' all, 'wing', 'delishop'.
	sourceFilter: '',
	provinceFilter: 'all', // all, 0 (Phnom Penh), 1 (Province)
	showAcceptedWithDriver: false, // For status 3 filtering

	// Sorting
	orderBy: 'status',
	order: 'ASC',

	// Selection
	selectedOrderIds: [],

	// UI State
	loading: false,
	error: null,
	detailsDialogOpen: false,
	exportDialogOpen: false,

	// Dialogs
	driverDialogOpen: false,
	walletDialogOpen: false,
	historyDialogOpen: false,
	updateSlotDialogOpen: false,
	deliveredAtDialogOpen: false,

	// Driver
	drivers: [],
	selectedDriver: null,

	// Update Slot
	updatingSlot: false,
	selectedSlotId: null,

	// Delivered At
	deliveredAt: null,

	// Bumped on every refresh so a refetch is triggered even when the filters
	// it resets were already at their defaults.
	refreshKey: 0,
};

// Action types
const actionTypes = {
	SET_ORDERS: 'SET_ORDERS',
	SET_ORDERS_RESULT: 'SET_ORDERS_RESULT',
	SET_FETCH_ERROR: 'SET_FETCH_ERROR',
	SET_SELECTED_ORDER: 'SET_SELECTED_ORDER',
	SET_ORDER_STATUS_COUNT: 'SET_ORDER_STATUS_COUNT',
	SET_PAGE: 'SET_PAGE',
	SET_ROWS_PER_PAGE: 'SET_ROWS_PER_PAGE',
	SET_TOTAL_COUNT: 'SET_TOTAL_COUNT',
	SET_STATUS_FILTER: 'SET_STATUS_FILTER',
	SET_SEARCH_TEXT: 'SET_SEARCH_TEXT',
	SET_DATE_RANGE: 'SET_DATE_RANGE',
	SET_BASKET_TYPE_FILTER: 'SET_BASKET_TYPE_FILTER',
	SET_SOURCE_FILTER: 'SET_SOURCE_FILTER',
	SET_PROVINCE_FILTER: 'SET_PROVINCE_FILTER',
	SET_SORTING: 'SET_SORTING',
	RESET_FILTERS: 'RESET_FILTERS',
	SET_SELECTED_ORDER_IDS: 'SET_SELECTED_ORDER_IDS',
	TOGGLE_ORDER_SELECTION: 'TOGGLE_ORDER_SELECTION',
	SELECT_ALL_ORDERS: 'SELECT_ALL_ORDERS',
	CLEAR_SELECTION: 'CLEAR_SELECTION',
	SET_LOADING: 'SET_LOADING',
	SET_ERROR: 'SET_ERROR',
	OPEN_DETAILS_DIALOG: 'OPEN_DETAILS_DIALOG',
	CLOSE_DETAILS_DIALOG: 'CLOSE_DETAILS_DIALOG',
	OPEN_EXPORT_DIALOG: 'OPEN_EXPORT_DIALOG',
	CLOSE_EXPORT_DIALOG: 'CLOSE_EXPORT_DIALOG',
	SET_DRIVERS: 'SET_DRIVERS',
	OPEN_DRIVER_DIALOG: 'OPEN_DRIVER_DIALOG',
	CLOSE_DRIVER_DIALOG: 'CLOSE_DRIVER_DIALOG',
	OPEN_WALLET_DIALOG: 'OPEN_WALLET_DIALOG',
	CLOSE_WALLET_DIALOG: 'CLOSE_WALLET_DIALOG',
	OPEN_HISTORY_DIALOG: 'OPEN_HISTORY_DIALOG',
	CLOSE_HISTORY_DIALOG: 'CLOSE_HISTORY_DIALOG',
	OPEN_UPDATE_SLOT_DIALOG: 'OPEN_UPDATE_SLOT_DIALOG',
	CLOSE_UPDATE_SLOT_DIALOG: 'CLOSE_UPDATE_SLOT_DIALOG',
	SET_UPDATING_SLOT: 'SET_UPDATING_SLOT',
	OPEN_DELIVERED_AT_DIALOG: 'OPEN_DELIVERED_AT_DIALOG',
	CLOSE_DELIVERED_AT_DIALOG: 'CLOSE_DELIVERED_AT_DIALOG',
	SOFT_REFRESH: 'SOFT_REFRESH',
};

// Reducer
// Any filter/sort change discards the current rows and goes straight into loading,
// so rows from the previous filter can never be shown under the new one, and the
// "No orders found" empty state can't flash in the gap before the refetch lands.
const resetForRefetch = {orders: [], totalCount: 0, selectedOrderIds: [], loading: true, error: null};

const ordersReducer = (state, action) => {
	switch (action.type) {
		case actionTypes.SET_ORDERS:
			return {...state, orders: action.payload};
		case actionTypes.SET_ORDERS_RESULT:
			// Land orders + totalCount + loading:false in one transition, so there is no
			// intermediate render where loading is false but the rows haven't arrived yet.
			return {
				...state,
				orders: action.payload.orders,
				totalCount: action.payload.totalCount,
				loading: false,
				error: null,
			};
		case actionTypes.SET_FETCH_ERROR:
			return {...state, error: action.payload, orders: [], totalCount: 0, loading: false};
		case actionTypes.SET_SELECTED_ORDER:
			return {...state, selectedOrder: action.payload};
		case actionTypes.SET_ORDER_STATUS_COUNT:
			return {...state, orderStatusCount: action.payload};
		case actionTypes.SET_PAGE:
			return {...state, ...resetForRefetch, page: action.payload};
		case actionTypes.SET_ROWS_PER_PAGE:
			return {...state, ...resetForRefetch, rowsPerPage: action.payload, page: 0};
		case actionTypes.SET_TOTAL_COUNT:
			return {...state, totalCount: action.payload};
		case actionTypes.SET_STATUS_FILTER:
			return {...state, ...resetForRefetch, statusFilter: action.payload, page: 0};
		case actionTypes.SET_SEARCH_TEXT:
			return {...state, ...resetForRefetch, searchText: action.payload, page: 0};
		case actionTypes.SET_DATE_RANGE:
			return {...state, ...resetForRefetch, dateRange: action.payload, page: 0};
		case actionTypes.SET_BASKET_TYPE_FILTER:
			return {...state, ...resetForRefetch, basketTypeFilter: action.payload, page: 0};
		case actionTypes.SET_SOURCE_FILTER:
			return {...state, ...resetForRefetch, sourceFilter: action.payload, page: 0};
		case actionTypes.SET_PROVINCE_FILTER:
			return {...state, ...resetForRefetch, provinceFilter: action.payload, page: 0};
		case actionTypes.SET_SORTING:
			return {...state, ...resetForRefetch, orderBy: action.payload.orderBy, order: action.payload.order};
		case actionTypes.RESET_FILTERS:
			return {
				...state,
				...resetForRefetch,
				statusFilter: initialState.statusFilter,
				searchText: initialState.searchText,
				dateRange: getDefaultDateRange(),
				basketTypeFilter: initialState.basketTypeFilter,
				sourceFilter: initialState.sourceFilter,
				provinceFilter: initialState.provinceFilter,
				showAcceptedWithDriver: initialState.showAcceptedWithDriver,
				orderBy: initialState.orderBy,
				order: initialState.order,
				page: 0,
				refreshKey: state.refreshKey + 1,
			};
		case actionTypes.SET_SELECTED_ORDER_IDS:
			return {...state, selectedOrderIds: action.payload};
		case actionTypes.TOGGLE_ORDER_SELECTION:
			const isSelected = state.selectedOrderIds.includes(action.payload);
			return {
				...state,
				selectedOrderIds: isSelected
					? state.selectedOrderIds.filter(id => id !== action.payload)
					: [...state.selectedOrderIds, action.payload],
			};
		case actionTypes.SELECT_ALL_ORDERS:
			return {
				...state,
				selectedOrderIds: state.orders.map(order => order.order_id),
			};
		case actionTypes.CLEAR_SELECTION:
			return {...state, selectedOrderIds: []};
		case actionTypes.SET_LOADING:
			return {...state, loading: action.payload};
		case actionTypes.SET_ERROR:
			return {...state, error: action.payload};
		case actionTypes.OPEN_DETAILS_DIALOG:
			return {...state, detailsDialogOpen: true, selectedOrder: action.payload};
		case actionTypes.CLOSE_DETAILS_DIALOG:
			return {...state, detailsDialogOpen: false};
		case actionTypes.OPEN_EXPORT_DIALOG:
			return {...state, exportDialogOpen: true};
		case actionTypes.CLOSE_EXPORT_DIALOG:
			return {...state, exportDialogOpen: false};
		case actionTypes.SET_DRIVERS:
			return {...state, drivers: action.payload};
		case actionTypes.OPEN_DRIVER_DIALOG:
			return {...state, driverDialogOpen: true, selectedOrder: action.payload};
		case actionTypes.CLOSE_DRIVER_DIALOG:
			return {...state, driverDialogOpen: false};
		case actionTypes.OPEN_WALLET_DIALOG:
			return {...state, walletDialogOpen: true, selectedOrder: action.payload};
		case actionTypes.CLOSE_WALLET_DIALOG:
			return {...state, walletDialogOpen: false};
		case actionTypes.OPEN_HISTORY_DIALOG:
			return {...state, historyDialogOpen: true, selectedOrder: action.payload};
		case actionTypes.CLOSE_HISTORY_DIALOG:
			return {...state, historyDialogOpen: false};
		case actionTypes.OPEN_UPDATE_SLOT_DIALOG:
			return {
				...state,
				updateSlotDialogOpen: true,
				selectedOrder: action.payload.order,
				selectedSlotId: action.payload.slotId
			};
		case actionTypes.CLOSE_UPDATE_SLOT_DIALOG:
			return {...state, updateSlotDialogOpen: false, selectedSlotId: null};
		case actionTypes.SET_UPDATING_SLOT:
			return {...state, updatingSlot: action.payload};
		case actionTypes.OPEN_DELIVERED_AT_DIALOG:
			return {
				...state,
				deliveredAtDialogOpen: true,
				selectedOrder: action.payload.order,
				deliveredAt: action.payload.deliveredAt,
				selectedSlotId: action.payload.slotId
			};
		case actionTypes.CLOSE_DELIVERED_AT_DIALOG:
			return {...state, deliveredAtDialogOpen: false, deliveredAt: null};
		case actionTypes.SOFT_REFRESH:
			return {
				...state,
				...resetForRefetch,
				refreshKey: state.refreshKey + 1,
			};
		default:
			return state;
	}
};

// Create context
const OrdersContext = createContext();

// Custom hook to use context
export const useOrders = () => {
	const context = useContext(OrdersContext);
	if (!context) {
		throw new Error('useOrders must be used within OrdersProvider');
	}
	return context;
};

// Provider component
const OrdersProvider = ({children}) => {
	const [state, dispatch] = useReducer(ordersReducer, initialState);
	const fetchRequestIdRef = useRef(0);

	// Fetch orders based on current filters
	const fetchOrders = useCallback(async () => {
		const requestId = ++fetchRequestIdRef.current;
		dispatch({type: actionTypes.SET_LOADING, payload: true});
		dispatch({type: actionTypes.SET_ERROR, payload: null});

		try {
			// Parse status filter - split digits like old implementation
			let tmpStatus = state.statusFilter;
			let statusList;
			let specialFilter = null;

			// Check if it's a special filter.
			// 2,3,4,5,8 is "not yet delivered" -- everything active except
			// delivered (6) and cancelled (7). The forDeliveryCount badge counts
			// exactly this set, so the two must not drift apart.
			if (tmpStatus === 'delivery-today') {
				// Plain status + date range on the server, so no specialFilter needed.
				statusList = [2, 3, 4, 5, 8];
			} else if (tmpStatus === 'late-orders') {
				// Late includes delivered (6): an order handed over after its timeslot is
				// still late, and the Late Orders card counts it ("Delivered past timeslot").
				specialFilter = 'late-orders';
				statusList = [2, 3, 4, 5, 6, 8];
			} else if (tmpStatus === 'pending-payments') {
				specialFilter = 'pending-payments';
				statusList = [2, 3, 4, 5, 6, 7, 8]; // All statuses 2-8
			} else if (tmpStatus === '-1') {
				statusList = [-1];
			} else if (tmpStatus === '58') {
				// In Transit = delivering (5) + arrived (8)
				statusList = [5, 8];
			} else {
				// Split the status string into individual digits and convert to numbers
				statusList = tmpStatus.toString().split('').map(x => parseInt(x));
			}

			// Format dates to YYYY-MM-DD only (no time)
			let formattedStartDate = '';
			let formattedEndDate = '';
			if (state.dateRange.start) {
				formattedStartDate = moment(state.dateRange.start).format('YYYY-MM-DD');
			}
			if (state.dateRange.end) {
				formattedEndDate = moment(state.dateRange.end).format('YYYY-MM-DD');
			}

			// "All" date filter: no window at all. Neither the real dates nor the
			// default fallback (yesterday -> tomorrow) may be sent, or the backend
			// would scope the result to that window instead of returning every date.
			const isAllDates = !formattedStartDate && !formattedEndDate;

			// Sorting by remaining prices the whole set server-side, so it is only
			// requested when the range is bounded (<= ~6 months). Otherwise fall back
			// to the default order and let the table sort the loaded rows itself.
			let orderField = state.orderBy;
			let orderType = state.order;
			if (orderField === 'remaining_amount' && !canServerSortRemaining(state.dateRange)) {
				orderField = 'status';
				orderType = 'ASC';
			}

			const query = {
				statusList: JSON.stringify(statusList),
				search: state.searchText,
				startDate: formattedStartDate,
				endDate: formattedEndDate,
				orderField,
				orderType,
				next: (state.page * state.rowsPerPage).toString(),
				limit: state.rowsPerPage.toString(),
			};

			if (isAllDates) {
				query.dateFilter = 'all';
			} else {
				// Default dates - yesterday through tomorrow
				const defaultRange = getDefaultDateRange();
				query.startDateDefault = moment(defaultRange.start).format('YYYY-MM-DD');
				query.endDateDefault = moment(defaultRange.end).format('YYYY-MM-DD');
			}

			if (state.basketTypeFilter) {
				query.type = state.basketTypeFilter;
			}

			if (state.sourceFilter) {
				query.sourceFilter = state.sourceFilter;
			}

			if (state.provinceFilter && state.provinceFilter !== 'all') {
				query.provinceFilter = state.provinceFilter;
			}

			// Add special filter to query if applicable
			if (specialFilter) {
				query.specialFilter = specialFilter;
			}

			// Use pagination for certain statuses
			const usePagination = ['45', '58', '6', '7', '-1', '2345678', 'delivery-today', 'late-orders', 'pending-payments'].includes(tmpStatus);

			const response = usePagination
				? await API.getOrderListPagination(query).then(res => res.json())
				: await API.getOrderListWithoutPagination(query).then(res => res.json());

			// Drop this response if a newer fetch has started since. Switching filters quickly
			// can leave an older, slower request in flight (the non-paginated statuses fetch the
			// whole set), and without this it would land last and overwrite the current filter.
			if (requestId !== fetchRequestIdRef.current) {
				return;
			}

			if (response.success) {
				let ordersList = response.data.list || [];

				// Filter for status 3 without driver if needed
				if (tmpStatus === '3' && !state.showAcceptedWithDriver) {
					ordersList = ordersList.filter(order => !order.driver_id);
				}

				dispatch({
					type: actionTypes.SET_ORDERS_RESULT,
					payload: {orders: ordersList, totalCount: response.data.total || ordersList.length},
				});
			} else {
				dispatch({type: actionTypes.SET_FETCH_ERROR, payload: response.message || 'Failed to fetch orders'});
			}
		} catch (error) {
			if (requestId !== fetchRequestIdRef.current) {
				return;
			}
			dispatch({type: actionTypes.SET_FETCH_ERROR, payload: error.message || 'An error occurred'});
		}
		// state.refreshKey is unused in the body on purpose: it exists only to make this
		// callback (and so the fetch effect) re-run when a reset leaves the filters unchanged.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state.statusFilter, state.searchText, state.dateRange, state.orderBy, state.order, state.page, state.rowsPerPage, state.basketTypeFilter, state.sourceFilter, state.provinceFilter, state.showAcceptedWithDriver, state.refreshKey]);

	// Fetch order status counts
	const fetchOrderStatusCount = useCallback(async () => {
		try {
			const dateParams = {};
			if (state.dateRange.start) {
				dateParams.startDate = moment(state.dateRange.start).format('YYYY-MM-DD');
			}
			if (state.dateRange.end) {
				dateParams.endDate = moment(state.dateRange.end).format('YYYY-MM-DD');
			}
			const response = await API.getOrderStatusCount(dateParams)
				.then(res => res.json());
			if (response.success) {
				dispatch({type: actionTypes.SET_ORDER_STATUS_COUNT, payload: response.data || {}});
			}
		} catch (error) {
			console.error('Failed to fetch order status count:', error);
		}
	}, [state.dateRange]);

	// Fetch drivers
	const fetchDrivers = useCallback(async () => {
		try {
			const response = await API.getDriverList({next: 0, limit: 10000, showType: false})
				.then(res => res.json());
			if (response.success) {
				dispatch({type: actionTypes.SET_DRIVERS, payload: response.data.list || []});
			}
		} catch (error) {
			console.error('Failed to fetch drivers:', error);
		}
	}, []);

	// Update order status
	const updateOrderStatus = useCallback(async (orderId, newStatus) => {
		dispatch({type: actionTypes.SET_LOADING, payload: true});
		try {
			const response = await API.changeBulkOrderStatus({
				order_ids: orderId.toString(),
				order_status: newStatus,
			}).then(res => res.json());

			if (response.success) {
				await fetchOrders();
				await fetchOrderStatusCount();
				return {success: true};
			} else {
				return {success: false, message: response.message};
			}
		} catch (error) {
			return {success: false, message: error.message};
		} finally {
			dispatch({type: actionTypes.SET_LOADING, payload: false});
		}
	}, [fetchOrders, fetchOrderStatusCount]);

	// Bulk update order status
	const bulkUpdateStatus = useCallback(async (orderIds, newStatus) => {
		dispatch({type: actionTypes.SET_LOADING, payload: true});
		try {
			// Convert array to pipe-separated string like old UI
			let idStr = "" + orderIds[0];
			for (let i = 1; i < orderIds.length; ++i) {
				idStr = idStr + "|" + orderIds[i];
			}

			const response = await API.changeBulkOrderStatus({
				order_ids: idStr,
				order_status: newStatus,
			}).then(res => res.json());

			if (response.success) {
				dispatch({type: actionTypes.CLEAR_SELECTION});
				await fetchOrders();
				await fetchOrderStatusCount();
				return {success: true};
			} else {
				return {success: false, message: response.message};
			}
		} catch (error) {
			return {success: false, message: error.message};
		} finally {
			dispatch({type: actionTypes.SET_LOADING, payload: false});
		}
	}, [fetchOrders, fetchOrderStatusCount]);

	// Generate invoice
	const generateInvoice = useCallback(async (orderId) => {
		try {
			const response = await API.generateInvoice({order_id: orderId});
			alert("Generating Invoice and Sending");
			return {success: true};
		} catch (error) {
			return {success: false, message: error.message};
		}
	}, []);

	// Bulk generate invoices
	const bulkGenerateInvoices = useCallback(async (orderIds) => {
		dispatch({type: actionTypes.SET_LOADING, payload: true});
		try {
			// Convert array to pipe-separated string like old UI
			let idStr = "" + orderIds[0];
			for (let i = 1; i < orderIds.length; ++i) {
				idStr = idStr + "|" + orderIds[i];
			}

			const response = await API.generateBulkInvoice({order_ids: idStr});
			if (response) {
				dispatch({type: actionTypes.CLEAR_SELECTION});
				await fetchOrders();
				await fetchOrderStatusCount();
				return {success: true};
			}
			return {success: false};
		} catch (error) {
			return {success: false, message: error.message};
		} finally {
			dispatch({type: actionTypes.SET_LOADING, payload: false});
		}
	}, [fetchOrders, fetchOrderStatusCount]);

	// Bulk create pickup file
	const bulkCreatePickupFile = useCallback(async (orderIds) => {
		dispatch({type: actionTypes.SET_LOADING, payload: true});
		try {
			// Convert array to pipe-separated string like old UI
			let idStr = "" + orderIds[0];
			for (let i = 1; i < orderIds.length; ++i) {
				idStr = idStr + "|" + orderIds[i];
			}

			const response = await API.createPickupFile({order_ids: idStr});
			if (response) {
				dispatch({type: actionTypes.CLEAR_SELECTION});
				await fetchOrders();
				await fetchOrderStatusCount();
				return {success: true};
			}
			return {success: false};
		} catch (error) {
			return {success: false, message: error.message};
		} finally {
			dispatch({type: actionTypes.SET_LOADING, payload: false});
		}
	}, [fetchOrders, fetchOrderStatusCount]);

	// Actions object
	const actions = {
		setPage: (page) => dispatch({type: actionTypes.SET_PAGE, payload: page}),
		setRowsPerPage: (rows) => dispatch({type: actionTypes.SET_ROWS_PER_PAGE, payload: rows}),
		setStatusFilter: (status) => dispatch({type: actionTypes.SET_STATUS_FILTER, payload: status}),
		setSearchText: (text) => dispatch({type: actionTypes.SET_SEARCH_TEXT, payload: text}),
		setDateRange: (range) => dispatch({type: actionTypes.SET_DATE_RANGE, payload: range}),
		setBasketTypeFilter: (type) => dispatch({type: actionTypes.SET_BASKET_TYPE_FILTER, payload: type}),
		setSourceFilter: (source) => dispatch({type: actionTypes.SET_SOURCE_FILTER, payload: source}),
		setProvinceFilter: (province) => dispatch({type: actionTypes.SET_PROVINCE_FILTER, payload: province}),
		setSorting: (orderBy, order) => dispatch({type: actionTypes.SET_SORTING, payload: {orderBy, order}}),
		toggleOrderSelection: (orderId) => dispatch({type: actionTypes.TOGGLE_ORDER_SELECTION, payload: orderId}),
		selectAllOrders: () => dispatch({type: actionTypes.SELECT_ALL_ORDERS}),
		clearSelection: () => dispatch({type: actionTypes.CLEAR_SELECTION}),
		openDetailsDialog: (order) => dispatch({type: actionTypes.OPEN_DETAILS_DIALOG, payload: order}),
		closeDetailsDialog: () => dispatch({type: actionTypes.CLOSE_DETAILS_DIALOG}),
		openExportDialog: () => dispatch({type: actionTypes.OPEN_EXPORT_DIALOG}),
		closeExportDialog: () => dispatch({type: actionTypes.CLOSE_EXPORT_DIALOG}),
		openDriverDialog: (order) => dispatch({type: actionTypes.OPEN_DRIVER_DIALOG, payload: order}),
		closeDriverDialog: () => dispatch({type: actionTypes.CLOSE_DRIVER_DIALOG}),
		openWalletDialog: (order) => dispatch({type: actionTypes.OPEN_WALLET_DIALOG, payload: order}),
		closeWalletDialog: () => dispatch({type: actionTypes.CLOSE_WALLET_DIALOG}),
		openHistoryDialog: (order) => dispatch({type: actionTypes.OPEN_HISTORY_DIALOG, payload: order}),
		closeHistoryDialog: () => dispatch({type: actionTypes.CLOSE_HISTORY_DIALOG}),
		openUpdateSlotDialog: (order, slotId) => dispatch({
			type: actionTypes.OPEN_UPDATE_SLOT_DIALOG,
			payload: {order, slotId}
		}),
		closeUpdateSlotDialog: () => dispatch({type: actionTypes.CLOSE_UPDATE_SLOT_DIALOG}),
		setUpdatingSlot: (value) => dispatch({type: actionTypes.SET_UPDATING_SLOT, payload: value}),
		openDeliveredAtDialog: (order, deliveredAt, slotId) => dispatch({
			type: actionTypes.OPEN_DELIVERED_AT_DIALOG,
			payload: {order, deliveredAt, slotId}
		}),
		closeDeliveredAtDialog: () => dispatch({type: actionTypes.CLOSE_DELIVERED_AT_DIALOG}),
		fetchOrders,
		fetchOrderStatusCount,
		fetchDrivers,
		updateOrderStatus,
		bulkUpdateStatus,
		generateInvoice,
		bulkGenerateInvoices,
		bulkCreatePickupFile,
		resetFilters: () => dispatch({type: actionTypes.RESET_FILTERS}),
		// Reloads the current view, keeping filters and page. Used after an order is
		// changed, where dropping the user's filters would lose their place.
		refreshData: () => {
			fetchOrders();
			fetchOrderStatusCount();
		},
		// Clears every filter and returns to page 1; the reset bumps refreshKey, so the
		// fetch effect reloads the orders itself rather than us firing a stale fetch here.
		resetAndRefresh: () => {
			dispatch({type: actionTypes.RESET_FILTERS});
			fetchOrderStatusCount();
		},
		// Reloads data keeping all current filters intact. Just refetches orders and counts.
		softRefresh: () => {
			dispatch({type: actionTypes.SOFT_REFRESH});
			fetchOrderStatusCount();
		},
	};

	// Initial data fetch
	useEffect(() => {
		fetchOrders();
		fetchOrderStatusCount();
		fetchDrivers();
	}, [fetchOrders, fetchOrderStatusCount, fetchDrivers]);

	return (
		<OrdersContext.Provider value={{state, actions}}>
			{children}
		</OrdersContext.Provider>
	);
};

export default OrdersProvider;
